import { useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import type { InfiniteData } from '@tanstack/react-query';
import { format, isAfter, sub } from 'date-fns';
import { useAtomValue } from 'jotai';

import { useGetDateFNSLocale } from '@/locales/helpers';
import type { ValidUniversalToothId } from '@videa/utils';
import { isPediatricToothId } from '@videa/utils';

import type {
  PatientListModes,
  PatientListSortFilterEnum,
  RecommendationFilter,
} from '../../@types/appointments';
import { SortFilterEnum, SortParam } from '../../@types/appointments';
import type { PreferencesResponse } from '../../@types/preferences';
import type {
  Appointment,
  GetAppointmentsResponse,
} from '../../react-query/useAppointments';
import type { GetTaskResponse } from '../../react-query/useGetTasks';
import { usePreferences } from '../../react-query/usePreferences';
import type {
  TProcedure,
  TVisitSummary,
} from '../../react-query/useVisitSummaries';
import { useLocalizedFormatDate } from '../claims/summary/state';
import { getPatientName } from '../uploader/patientList/patientListTableContainer/utils';

import {
  operatoryFilterAtom,
  patientListViewAtom,
  selectedCountsFilterAtom,
  selectedProvidersAtom,
} from './atoms';
import {
  getMatchingPrimaryTooth,
  getRecommendedProcedures,
  isRecommendationFilter,
  isTaskFilter,
} from './patientStrip/patientStripOdontogram/utils';
import { useShouldHideHuddle } from './useShouldHideHuddle';

export const getDisplayName = (appointment: Appointment, index: number) => {
  const { firstName, lastName } = appointment.patient;
  let patientName = '';
  if (firstName) {
    patientName = firstName;
    if (lastName) {
      patientName += ` ${lastName}`;
    }
  }
  patientName = getPatientName(patientName, index, true);
  const initials = patientName.split(' ').map((name: any) => name[0]);

  return { patientName, initials };
};

export const getUserInitials = (user?: {
  firstName?: string;
  lastName?: string;
}) => {
  const first = user?.firstName?.[0] ?? '';
  const last = user?.lastName?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
};

export const useAppointmentsFilteredByProviderAndOperatory = (
  appointments?: Appointment[],
) => {
  const selectedOperatories = useAtomValue(operatoryFilterAtom);
  const selectedProviders = useAtomValue(selectedProvidersAtom);

  return useMemo(() => {
    const seenUnifiedIds = new Set<string>();

    return [...(appointments ?? [])].filter(({ appointment }) => {
      const { operatory, provider, unifiedId } = appointment;

      if (seenUnifiedIds.has(unifiedId)) {
        return false;
      }
      seenUnifiedIds.add(unifiedId);
      const isOperatorySelected =
        operatory?.name && selectedOperatories.includes(operatory.name);
      const isProviderSelected =
        provider?.id && selectedProviders.some((p) => p.id === provider.id);

      if (selectedOperatories.length && selectedProviders.length) {
        return isOperatorySelected && isProviderSelected;
      } else if (selectedOperatories.length) {
        return isOperatorySelected;
      } else if (selectedProviders.length) {
        return isProviderSelected;
      }
      return true;
    });
  }, [appointments, selectedOperatories, selectedProviders]);
};

export const useAppointmentsFilteredByTasksRecommendations = (
  appointments: Appointment[],
  appointmentIdsToTasks: Record<string, GetTaskResponse>,
  preferences?: PreferencesResponse,
  visitSummaries?: TVisitSummary[],
) => {
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);

  return useMemo(() => {
    if (isTaskFilter(selectedCountsFilter)) {
      return [...appointments].filter((appointment) => {
        const tasks =
          appointmentIdsToTasks[appointment.appointment.unifiedId]?.tasks;
        const currentTask = tasks?.find(
          (task) => task.type === selectedCountsFilter,
        );
        return currentTask?.status === 'SUGGESTED';
      });
    }

    if (isRecommendationFilter(selectedCountsFilter)) {
      return [...appointments].filter(({ appointment }) => {
        const visitSummary = visitSummaries?.find(
          (vs) => vs.estimatedVisitId === appointment.estimatedVisitId,
        );
        const visitRecommendations = getVisitRecommendations(
          visitSummary,
          preferences,
        );
        if (selectedCountsFilter === 'ALL_OPPORTUNITIES') {
          return Object.values(visitRecommendations).some((value) => value);
        }

        if (selectedCountsFilter === 'perio') {
          return visitRecommendations.perio || visitRecommendations.locPerio;
        }

        return visitRecommendations[selectedCountsFilter];
      });
    }

    return appointments;
  }, [
    appointmentIdsToTasks,
    appointments,
    preferences,
    selectedCountsFilter,
    visitSummaries,
  ]);
};

export const getSortParam = (
  view: PatientListModes,
  sortFilter: PatientListSortFilterEnum,
): SortParam => {
  if (sortFilter.includes(SortFilterEnum.APPOINTMENT_TIME_LATEST)) {
    return SortParam.descending;
  }

  if (view === 'DAILY_HUDDLE') {
    return SortParam.ascending;
  }

  return SortParam.descending;
};

export const useIsScheduleViewEnabled = () => {
  const selectedTab = useAtomValue(patientListViewAtom);
  const hideTabs = useShouldHideHuddle();
  return selectedTab === 'HUDDLE' && !hideTabs;
};

export const useListView = () => {
  return useAtomValue(patientListViewAtom);
};

export const findNewestAnalyses = (
  appointmentData: InfiniteData<GetAppointmentsResponse> | undefined,
  latestAnalysisDate: string | null,
) => {
  const prevAnalysisDate = latestAnalysisDate
    ? latestAnalysisDate
    : sub(new Date(), {
        minutes: 15,
      }).toISOString();

  if (!appointmentData) {
    return {
      hasNewerAnalyses: false,
      newerUnifiedId: null,
      updatedAnalysisDate: latestAnalysisDate,
    };
  }
  const flatAppointments = appointmentData?.pages.flatMap((page) => page.data);
  const newestDate =
    !!flatAppointments &&
    flatAppointments
      .filter((appointment) => !!appointment.appointment.imagesAt)
      .map((appointment) => appointment.appointment.imagesAt)
      .sort()
      .reverse()[0];
  const newestAnalysis =
    !!flatAppointments &&
    flatAppointments.find(
      (appointment) => appointment.appointment.imagesAt === newestDate,
    );
  const hasNewestAnalysisNotBeenViewed =
    !!newestAnalysis && !newestAnalysis?.viewed;
  const newestUnifiedId =
    !!newestAnalysis && newestAnalysis?.appointment.unifiedId;
  if (
    !!newestDate &&
    !!newestUnifiedId &&
    hasNewestAnalysisNotBeenViewed &&
    isAfter(new Date(newestDate), new Date(prevAnalysisDate))
  ) {
    return {
      hasNewerAnalyses: true,
      newerUnifiedId: newestUnifiedId,
      updatedAnalysisDate: newestDate,
    };
  }
  return {
    hasNewerAnalyses: false,
    newerUnifiedId: null,
    updatedAnalysisDate: prevAnalysisDate,
  };
};

export const getVisitRecommendations = (
  visitSummary?: TVisitSummary,
  preferences?: PreferencesResponse,
) => {
  const accumulator: Record<TProcedure['name'], boolean> = {
    crown: false,
    enamelRegen: false,
    extraction: false,
    filling: false,
    implant: false,
    aligner: false,
    other: false,
    perio: false,
    rootCanal: false,
    locPerio: false,
  };

  if (!visitSummary) return accumulator;

  const mapRecommendedProcedures = (
    data:
      | TVisitSummary['teeth'][number]
      | TVisitSummary['quadrants' | 'arches']
      | TVisitSummary['mouth'],
  ) => {
    const items = Array.isArray(data) ? data : [data];
    items
      .flatMap((item) =>
        getRecommendedProcedures(
          item,
          preferences,
          true,
          visitSummary?.isPerioPatient,
        ),
      )
      .forEach(({ name }) => {
        accumulator[name] = true;
      });
  };

  visitSummary.teeth.forEach((tooth) => {
    const matchingPrimaryTooth = getMatchingPrimaryTooth(
      tooth.toothId as ValidUniversalToothId,
      visitSummary,
    );
    // Don't include recommendations on permanent teeth if there is a
    // corresponding primary tooth
    if (isPediatricToothId(tooth.toothId) || !matchingPrimaryTooth) {
      mapRecommendedProcedures(tooth);
    }
  });
  mapRecommendedProcedures(visitSummary.quadrants);
  mapRecommendedProcedures(visitSummary.arches);
  mapRecommendedProcedures(visitSummary.mouth);

  return accumulator;
};

export const useFormatAppointmentDate = (date: string) => {
  const dateFNSLocale = useGetDateFNSLocale();
  const formatLocalizedDate = useLocalizedFormatDate();
  const { t } = useLingui();

  const appointmentDate = new Date(date);
  const today = new Date();
  const isToday = appointmentDate.toDateString() === today.toDateString();

  const formattedTime = format(appointmentDate, 'h:mmaaa', {
    locale: dateFNSLocale,
  });

  return isToday
    ? formattedTime
    : `${formatLocalizedDate(date, true)} ${t`at`} ${formattedTime}`;
};

const useVisitSummariesFilteredByProviderAndOperatory = (
  visitSummaries?: TVisitSummary[],
  appointments?: Appointment[],
) => {
  const selectedProviders = useAtomValue(selectedProvidersAtom);
  const selectedOperatories = useAtomValue(operatoryFilterAtom);

  return (visitSummaries ?? []).filter((visitSummary) => {
    const appointment = appointments?.find(
      (a) => a.appointment.estimatedVisitId === visitSummary.estimatedVisitId,
    );

    if (!appointment) {
      return false;
    }

    const isFilteredByProvider = !!selectedProviders.length;
    const isFilteredByOperatory = !!selectedOperatories.length;

    if (!isFilteredByProvider && !isFilteredByOperatory) {
      return true;
    }

    const isProviderSelected = selectedProviders.some(
      (p) => p.id === appointment?.appointment.provider?.id,
    );
    const isOperatorySelected = selectedOperatories.some(
      (o) => o === appointment?.appointment.operatory?.name,
    );

    if (isFilteredByProvider && isFilteredByOperatory) {
      return isProviderSelected && isOperatorySelected;
    }
    if (isFilteredByProvider) {
      return isProviderSelected;
    }
    return isOperatorySelected;
  });
};

export const useRecommendationFilterCounts = (
  visitSummaries?: TVisitSummary[],
  appointments?: Appointment[],
) => {
  const { data: prefs } = usePreferences();
  const visitSummariesFiltered =
    useVisitSummariesFilteredByProviderAndOperatory(
      visitSummaries,
      appointments,
    );

  return useMemo(() => {
    const accumulator: Record<RecommendationFilter, Set<number>> = {
      crown: new Set(),
      enamelRegen: new Set(),
      extraction: new Set(),
      filling: new Set(),
      implant: new Set(),
      aligner: new Set(),
      other: new Set(),
      perio: new Set(),
      locPerio: new Set(),
      rootCanal: new Set(),
      ALL_OPPORTUNITIES: new Set(),
    };

    const sets = visitSummariesFiltered.reduce((acc, visitSummary) => {
      const recommendations = getVisitRecommendations(visitSummary, prefs);
      const appointmentId = visitSummary.unifiedAppointmentId;

      Object.entries(recommendations).forEach(([key, isRecommended]) => {
        if (isRecommended) {
          acc[key as RecommendationFilter].add(appointmentId);
          acc.ALL_OPPORTUNITIES.add(appointmentId);
        }
      });

      return acc;
    }, accumulator);

    // Convert Sets to counts, combining perio and locPerio
    const counts = Object.fromEntries(
      Object.entries(sets).map(([key, set]) => [key, set.size]),
    ) as Record<RecommendationFilter, number>;

    // Combine perio and locPerio counts
    const combinedPerioSet = new Set([...sets.perio, ...sets.locPerio]);
    counts.perio = combinedPerioSet.size;

    return counts as Record<RecommendationFilter, number>;
  }, [prefs, visitSummariesFiltered]);
};

// Takes in a date like 2024-04-20 and converts to local date object
export function createLocalDate(dateString: string) {
  const parts = dateString.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  return new Date(year, month, day);
}
